import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";

export interface ResponseDetails {
  engagementName: string;
  engagementCode: string;
  opportunityCode: string;
}

interface EngagementNameState {
  isLoading: boolean;
  data: ResponseDetails | null;
  error: string | null;
  isLoadingException: boolean;
  dataException: ResponseDetails | null;
  errorException: string | null;
}

const initialState: EngagementNameState = {
  isLoading: false,
  data: null,
  error: null,
  isLoadingException: false,
  dataException: null,
  errorException: null,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any

interface RequestDetailsEngagementName {
  Action: string;
  EngagementCode: string; // Optional for initial fetch
}

export const fetchEngagementName = createAsyncThunk<
  any,
  RequestDetailsEngagementName
>(
  "EngagementNameSlice/fetchEngagementNameData",
  async (requestDetails, thunkApi) => {
    const requestParam = {
      Action: requestDetails.Action,
      EngagementCode: requestDetails.EngagementCode,
    };

    try {
      const response = await httpClient.post("/order", requestParam);

      if (response.status != 200 || response.data.Status !== "success") {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to get engagement data. Please try again.",
          });
        }
      }
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message: error?.response?.data?.Message
          ? error.response.data.Message
          : "Failed to get engagement data. Please try again.",
      });
    }
  },
);

interface RequestDetailsEngagementNameException {
  Action: string;
  EngagementCodeException: string; // Optional for initial fetch
}

export const fetchEngagementNameException = createAsyncThunk<
  any,
  RequestDetailsEngagementNameException
>(
  "EngagementNameSlice/fetchEngagementNameExceptionData",
  async (requestDetails, thunkApi) => {
    const requestParam = {
      Action: requestDetails.Action,
      EngagementCode: requestDetails.EngagementCodeException,
    };

    try {
      const response = await httpClient.post("/order", requestParam);

      if (response.status != 200 || response.data.Status !== "success") {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message:
              "Failed to get engagement exception data. Please try again.",
          });
        }
      }
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message: error?.response?.data?.Message
          ? error.response.data.Message
          : "Failed to get engagement exception data. Please try again.",
      });
    }
  },
);

const EngagementNameSlice = createSlice({
  name: "EngagementNameSlice",
  initialState,
  reducers: {
    resetEngagementNameState: (state) => {
      state.isLoading = false;
      state.data = null;
      state.error = null;
    },
    resetEngagementNameExceptionState: (state) => {
      state.isLoadingException = false;
      state.dataException = null;
      state.errorException = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEngagementName.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        fetchEngagementName.fulfilled,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          if (action.payload.Status == "success") {
            state.isLoading = false;
            state.data = action.payload.Data;
            state.error = null;
          } else if (action.payload.Status == "error") {
            state.isLoading = false;
            state.data = null;
            state.error = action.payload.message;
          }
        },
      )
      .addCase(
        fetchEngagementName.rejected,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.data = null;
          state.error = action.payload.message;
        },
      )
      .addCase(fetchEngagementNameException.pending, (state) => {
        state.isLoadingException = true;
        state.errorException = null;
        state.dataException = null;
      })
      .addCase(
        fetchEngagementNameException.fulfilled,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          if (action.payload.Status == "success") {
            state.isLoadingException = false;
            state.dataException = action.payload.Data;
            state.errorException = null;
          } else if (action.payload.Status == "error") {
            state.isLoadingException = false;
            state.dataException = null;
            state.errorException = action.payload.message;
          }
        },
      )
      .addCase(
        fetchEngagementNameException.rejected,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          state.isLoadingException = false;
          state.dataException = null;
          state.errorException = action.payload.message;
        },
      );
  },
});

export const { resetEngagementNameState, resetEngagementNameExceptionState } =
  EngagementNameSlice.actions;

export default EngagementNameSlice.reducer;
